'use client';

import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { UserIcon } from '@heroicons/react/24/outline';
import { logger } from '@/lib/platform/utils/logger';

interface SubscriptionBannerProps {
  status: 'trial' | 'expired' | 'payment_failed' | null;
  daysRemaining?: number;
  // Impersonation mode
  isImpersonating?: boolean;
  impersonatedUserEmail?: string;
  onStopImpersonating?: () => void;
  stoppingImpersonation?: boolean;
}

export default function SubscriptionBanner({
  isImpersonating = false,
  impersonatedUserEmail,
  onStopImpersonating,
  stoppingImpersonation = false,
}: SubscriptionBannerProps) {
  const { darkMode } = useDarkMode();

  logger.log('[SubscriptionBanner] Rendering - isImpersonating:', isImpersonating);

  // Only show banner when impersonating
  if (!isImpersonating) {
    return null;
  }

  return (
    <div className={`w-full ${darkMode ? 'bg-orange-600' : 'bg-orange-500'}`}>
      <div className="flex items-center gap-x-6 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
        <p className="flex items-center text-sm/6 text-white">
          <UserIcon className="mr-2 inline-block size-5" />
          <strong className="font-semibold">You are impersonating:</strong>
          <span className="ml-2">{impersonatedUserEmail || 'Unknown User'}</span>
        </p>
        <div className="flex flex-1 justify-end">
          <button
            type="button"
            onClick={onStopImpersonating}
            disabled={stoppingImpersonation}
            className="rounded-md bg-white/20 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-white/30 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {stoppingImpersonation ? 'Stopping...' : 'Stop Impersonating'}
          </button>
        </div>
      </div>
    </div>
  );
}
